/**
 * A camera's name in the reader's language, and bidi isolation for
 * user-entered text inside composed strings.
 *
 * Every camera carries two names (the edit form's "English name" and
 * "Hebrew name"). The Hebrew UI shows the Hebrew one; every other language
 * the English one, falling back to whichever exists — the same rule
 * `siteDisplayName` applies to the site.
 */
import { useCallback } from "react";
import { useLang } from "@/hooks/useDir";
import type { Camera } from "@/types/cameras";

type Named = Pick<Camera, "name" | "name_he">;

export function cameraDisplayName(c: Named, lang: string): string {
  const he = (c.name_he ?? "").trim();
  const en = (c.name ?? "").trim();
  return lang === "he" ? he || en : en || he;
}

/** `nameOf(camera)` bound to the active language. */
export function useCameraName(): (c: Named) => string {
  const lang = useLang();
  return useCallback((c: Named) => cameraDisplayName(c, lang), [lang]);
}

const FSI = "⁨"; // FIRST STRONG ISOLATE
const PDI = "⁩"; // POP DIRECTIONAL ISOLATE

/**
 * Wrap user-entered text (a camera, zone or place name) that goes into a
 * composed string, so its direction cannot leak into the words around it —
 * the plain-text equivalent of <bdi>. Without it a Hebrew camera name in an
 * English line swallows the number next to it: "<name> · 13 min. ago" came
 * out as "13 · <name> min. ago".
 */
export function isolate(text: string): string {
  return text ? `${FSI}${text}${PDI}` : text;
}

/** Join parts with " · ", each one isolated. Empty parts are dropped. */
export function joinIsolated(parts: Array<string | null | undefined | false>): string {
  return parts
    .filter((p): p is string => typeof p === "string" && p.length > 0)
    .map(isolate)
    .join(" · ");
}
